import { Dispatch, FunctionComponent, SetStateAction, useCallback } from "react";

import {
	CompanyEnrichAutoComplete,
	CompanyEnrichAutoCompleteItem,
} from "@/pages/Private/pages/CompanyEnrich/components/CompanyEnrichAutoComplete";
import {
	useGetGeoCountriesMutation,
	useGetGeoStatesMutation,
	useGetGeoCitiesMutation,
} from "@/pages/Private/redux/companyEnrich/companyEnrich.api";

interface Props {
	companyCountries: { id: string; name: string }[];
	setCompanyCountries: Dispatch<SetStateAction<{ id: string; name: string }[]>>;
	companyStates: { id: number; name: string }[];
	setCompanyStates: Dispatch<SetStateAction<{ id: number; name: string }[]>>;
	companyCities: { id: number; name: string }[];
	setCompanyCities: Dispatch<SetStateAction<{ id: number; name: string }[]>>;
}

export const ByFiltersAdvancedSection: FunctionComponent<Props> = ({
	companyCountries,
	setCompanyCountries,
	companyStates,
	setCompanyStates,
	companyCities,
	setCompanyCities,
}) => {
	// ── Hooks ──────────────────────────────────────────────────────────────────
	const [getGeoCountries] = useGetGeoCountriesMutation();
	const [getGeoStates] = useGetGeoStatesMutation();
	const [getGeoCities] = useGetGeoCitiesMutation();

	// ── Fetch callbacks ────────────────────────────────────────────────────────
	const fetchCompanyCountries = useCallback(
		async (query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			const result = await getGeoCountries({ query }).unwrap();

			return result.items.map((item) => ({ id: item.code, title: item.name }));
		},
		[getGeoCountries]
	);

	const fetchStates = useCallback(
		async (query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			if (!companyCountries.length) {
				return [];
			}

			const codes = companyCountries.map((c) => c.id);
			const result = await getGeoStates({ query, countryCodes: codes }).unwrap();

			return result.items.map((item) => ({ id: String(item.id), title: item.name }));
		},
		[getGeoStates, companyCountries]
	);

	const fetchCities = useCallback(
		async (query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			if (!companyCountries.length) {
				return [];
			}

			const codes = companyCountries.map((c) => c.id);
			const result = await getGeoCities({ query, countryCodes: codes }).unwrap();

			return result.items.map((item) => ({ id: String(item.id), title: item.name }));
		},
		[getGeoCities, companyCountries]
	);

	return (
		<>
			<CompanyEnrichAutoComplete
				stayOn
				fetchFn={fetchCompanyCountries}
				handleSelect={(value) =>
					setCompanyCountries(value?.map((r) => ({ id: r.id, name: r.title })) || [])
				}
				label="Company country"
				placeholder="Search countries..."
				selectedItems={companyCountries.map((c) => ({ id: c.id, title: c.name }))}
			/>
			<CompanyEnrichAutoComplete
				stayOn
				fetchFn={fetchStates}
				handleSelect={(value) =>
					setCompanyStates(value?.map((r) => ({ id: Number(r.id), name: r.title })) || [])
				}
				label="Company state"
				placeholder="Search states..."
				selectedItems={companyStates.map((s) => ({ id: String(s.id), title: s.name }))}
			/>
			<CompanyEnrichAutoComplete
				stayOn
				fetchFn={fetchCities}
				handleSelect={(value) =>
					setCompanyCities(value?.map((r) => ({ id: Number(r.id), name: r.title })) || [])
				}
				label="Company city"
				placeholder="Search cities..."
				selectedItems={companyCities.map((c) => ({ id: String(c.id), title: c.name }))}
			/>
		</>
	);
};
